import React, { createContext, useReducer } from 'react';
import { User } from './__generated__/graphql';
import client from './apollo';
import api from './common/api';
import {
  CURRENT_EMAIL,
  REFRESH_TOKEN,
  TOKEN,
  USER,
  WORKSPACE_KEY,
} from './common/constants';
import { setItem } from './common/utils';
import {
  AppAction,
  AppActionType,
  AppContextType,
  AppState,
} from './types/appContext.type';
import { ChildrenPropType } from './types/common.type';

const getLoggedInUser = (): User | null => {
  const loggedInUser = localStorage.getItem(USER);
  return loggedInUser ? JSON.parse(loggedInUser) : null;
};

const initialState: AppState = {
  currentUser: getLoggedInUser() || {},
  authenticated: !!localStorage.getItem(TOKEN),
  authToken: localStorage.getItem(TOKEN),
  userEmail: localStorage.getItem(CURRENT_EMAIL) ?? '',
  workspace: localStorage.getItem(WORKSPACE_KEY) ?? null,
};

const reducer = (state: AppState, action: AppAction) => {
  switch (action?.type) {
    case AppActionType.setCurrentUser:
      // eslint-disable-next-line no-case-declarations
      const user = action.data || {};
      localStorage.setItem(
        USER,
        user && Object?.keys(user)?.length ? JSON?.stringify(user) : '',
      );
      return { ...state, currentUser: { ...user } };
    case AppActionType.setCurrentRole:
      return { ...state, currentRole: action.data };
    case AppActionType.logout:
      delete api?.defaults?.headers?.common?.Authorization;
      localStorage.clear();
      client?.clearStore();
      return {
        ...initialState,
        authenticated: false,
        authToken: null,
        user: {},
      };
    case AppActionType.setAuthenticated:
      return { ...state, authenticated: action.data };
    case AppActionType.setAuthToken:
      localStorage.setItem(TOKEN, action.data ?? '');
      return { ...state, authToken: action.data };
    case AppActionType.setRefreshToken:
      localStorage.setItem(REFRESH_TOKEN, action.data ?? '');
      return {
        ...state,
        refreshToken: action.data,
      };
    case AppActionType.setWorkspace:
      setItem(WORKSPACE_KEY, action?.data ? JSON.stringify(action?.data) : '');
      return { ...state, workspace: action?.data };
    case AppActionType.setEmail:
      return { ...state, userEmail: action?.data };
    default:
      return { ...state };
  }
};

const AppContext = createContext<AppContextType | null>(null);

const AppContextProvider: React.FC<ChildrenPropType> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const getToken = () => localStorage.getItem(TOKEN) || null;
  const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN);
  const getCurrentUser = () =>
    localStorage?.getItem(USER)
      ? JSON?.parse(localStorage.getItem(USER) ?? '')
      : {};

  const isAuthenticated = () => state.authenticated;

  const initializeAuth = (
    authToken: string | null,
    userData: User | null,
    refreshToken?: string | null,
  ) => {
    const token = authToken || getToken();
    const user = userData || getCurrentUser();
    const refresh = refreshToken || getRefreshToken();
    if (token) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
      dispatch({ type: AppActionType.setAuthToken, data: token });
      dispatch({ type: AppActionType.setRefreshToken, data: refresh });
      dispatch({ type: AppActionType.setAuthenticated, data: true });
      dispatch({ type: AppActionType.setCurrentUser, data: user });
    }
  };

  const value: AppContextType = {
    state,
    dispatch,
    isAuthenticated,
    getToken,
    getRefreshToken,
    initializeAuth,
    getCurrentUser,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

const AppContextConsumer = AppContext.Consumer;

export { AppContext, AppContextConsumer, AppContextProvider };
